import { useCallback, useEffect, useMemo, useState } from "react";
import type { InspectionState } from "../../InspectionsLists.d";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import { useLanguage } from "@/context/language-context/LanguageContext";
import { cn } from "@/lib/styles.utils";
import { Icon } from "@/components/icons/Icon";
import { getIsFiltered } from "../../utils/inspections.utils";
import { useUserStore } from "@/store/useUser";

interface TableHeaderProps {
  tableType: InspectionState;
  canSort: boolean;
  lastFiltered: string;
  searchInspections: (params: string) => void;
  getNoSortedInspections: () => void;
}

const TableHeader = ({
  tableType = "active",
  canSort,
  lastFiltered,
  searchInspections,
  getNoSortedInspections,
}: TableHeaderProps) => {
  const translate = useTranslate();
  const language = useLanguage();
  const { isSupervisor } = useUserStore();

  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("");

  useEffect(() => {
    if (!lastFiltered?.includes("orderBy=")) {
      setSortBy("");
      setSortDirection("");
    }
  }, [lastFiltered]);

  const headers = useMemo(() => {
    const _createElements = (
      elements: { name: string; widthClasses?: string }[],
    ) => {
      const headerElements = [];
      const elementsLength = elements.length;
      for (let index = 0; index < elementsLength; index += 1) {
        headerElements.push({
          name: translate(elements[index].name),
          widthClasses: elements[index].widthClasses,
          sortParam: elements[index].name,
        });
      }
      return headerElements;
    };

    if (tableType === "active") {
      return _createElements([
        {
          name: "startDate",
          widthClasses: isSupervisor
            ? "min-w-[150px] xl:min-w-[170px]"
            : "min-w-[150px]",
        },
        {
          name: "engineType",
          widthClasses: isSupervisor
            ? "min-w-[120px] xl:min-w-[140px]"
            : "min-w-[120px]",
        },
        {
          name: "snStand",
          widthClasses: isSupervisor
            ? "min-w-[120px] xl:min-w-[140px]"
            : "min-w-[120px]",
        },
        {
          name: "esnEnter",
          widthClasses: isSupervisor
            ? "min-w-[120px] xl:min-w-[140px]"
            : "min-w-[120px]",
        },
        {
          name: "colour",
          widthClasses: isSupervisor
            ? "min-w-[120px] xl:min-w-[140px]"
            : "min-w-[120px]",
        },
        {
          name: "phase",
          widthClasses: isSupervisor
            ? "min-w-[250px] xl:min-w-[270px] w-[250px] xl:w-[270px]"
            : "min-w-[250px] w-[250px]",
        },
        {
          name: " ",
          widthClasses: "min-w-[64px]",
        },
      ]);
    }
    return _createElements([
      {
        name: "startDate",
        widthClasses: isSupervisor
          ? "min-w-[150px] xl:min-w-[170px]"
          : "min-w-[150px]",
      },
      {
        name: "endDate",
        widthClasses: isSupervisor
          ? "min-w-[165px] xl:min-w-[190px]"
          : "min-w-[165px]",
      },
      {
        name: "engineType",
        widthClasses: isSupervisor
          ? "min-w-[130px] xl:min-w-[140px]"
          : "min-w-[130px]",
      },
      {
        name: "snStand",
        widthClasses: isSupervisor
          ? "min-w-[130px] xl:min-w-[140px]"
          : "min-w-[130px]",
      },
      {
        name: "esnExit",
        widthClasses: isSupervisor
          ? "min-w-[130px] xl:min-w-[140px]"
          : "min-w-[130px]",
      },
      {
        name: "colour",
        widthClasses: isSupervisor
          ? "min-w-[130px] xl:min-w-[140px]"
          : "min-w-[130px]",
      },
      {
        name: " ",
        widthClasses: isSupervisor
          ? "min-w-[130px] xl:min-w-[140px]"
          : "min-w-[130px]",
      },
    ]);
  }, [tableType, language, isSupervisor, translate]);

  const searchSortedInspections = useCallback(
    (sortCriteria: string) => {
      let sorter = "";
      switch (sortCriteria) {
        case "startDate":
          sorter = "createdAt";
          break;
        case "engineType":
          sorter = "engineName";
          break;
        case "snStand":
          sorter = "nsStand";
          break;
        case "esnEnter":
          sorter = "esnIn";
          break;
        case "esnExit":
          sorter = "esnOut";
          break;
        case "phase":
          sorter = "phaseName";
          break;
        default:
          sorter = sortCriteria;
          break;
      }

      const changeToNoOrder = lastFiltered.includes(
        `orderBy=${sorter}&order=desc`,
      );

      const isFiltered = getIsFiltered(lastFiltered);
      if (changeToNoOrder && !isFiltered) {
        setSortBy("");
        setSortDirection("");
        getNoSortedInspections();
      } else {
        let order = !lastFiltered.includes(`orderBy=${sorter}&order=asc`)
          ? "asc"
          : "desc";
        if (changeToNoOrder) {
          order = "";
          setSortBy("");
          setSortDirection("");
        } else {
          setSortBy(sortCriteria);
          setSortDirection(order);
        }
        const sortParams = changeToNoOrder
          ? "&page="
          : `&orderBy=${sorter}&order=${order}&page=`;
        const noSortedLastFiltered = lastFiltered.includes("&orderBy=")
          ? lastFiltered.split("&orderBy=")[0]
          : lastFiltered;
        let filterParams = noSortedLastFiltered.includes("inspections?")
          ? noSortedLastFiltered.split("inspections?")[1].replace("page=", "")
          : "";
        if (!filterParams.length)
          filterParams = `status=${
            tableType === "active" ? "IN_PROGRESS" : " CLOSED"
          }&size=20`;

        const params = `?${filterParams}${sortParams}`.replaceAll("&&", "&");

        searchInspections(params);
      }
    },
    [searchInspections, lastFiltered, tableType, getNoSortedInspections],
  );

  return (
    <tr className="font-[IberiaTextBold]">
      <th className="bg-ib-gray-lightest border-b border-b-ib-gray-lightest rounded-tl-[6px] text-left w-11 min-w-11 max-w-11 h-14"></th>
      {headers.map((header, index) => (
        <th
          key={`th-${index}`}
          className={
            index === headers.length - 1
              ? `right-header ${header.widthClasses}`
              : `central-header ${header.widthClasses}`
          }
        >
          <div className="w-full flex flex-row justify-center items-center">
            <div>{header.name}</div>
            {header.name.trim().length > 0 && canSort ? (
              <button
                onClick={() => searchSortedInspections(header.sortParam)}
                className={cn(
                  sortBy === header.sortParam ? "text-ib-red-main" : "",
                )}
              >
                {sortBy === header.sortParam ? (
                  <Icon
                    name="backArrow"
                    className={cn(
                      "size-6 mx-2",
                      sortDirection === "asc" ? "rotate-90" : "-rotate-90",
                    )}
                  />
                ) : (
                  <Icon name="sortArrows" className="size-6 mx-2" />
                )}
              </button>
            ) : null}
          </div>
        </th>
      ))}
    </tr>
  );
};

export default TableHeader;
